import React, { useEffect, useRef } from 'react';
import './about.css';
import { gsap } from 'gsap';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF } from '@react-three/drei';

function DroneModel({ url }) {
  const { scene } = useGLTF(url)
  return <primitive object={scene} />
}

function AnimatedText({ text, random = false }) {
  const textRef = useRef();
  useEffect(() => {
    const words = Array.from(textRef.current.children);
    let order = words.map((_, i) => i);
    if (random) {
      order = order.sort(() => Math.random() - 0.5);
    }
    order.forEach((idx, i) => {
      // Randomize animation properties if random is true
      const animProps = random
        ? {
            opacity: 0,
            y: Math.floor(Math.random() * 60) - 30,
            x: Math.floor(Math.random() * 40) - 20,
            scale: 0.8 + Math.random() * 0.4,
            rotate: Math.floor(Math.random() * 16) - 8,
          }
        : { opacity: 0, y: 32, scale: 0.95, rotate: -2 };
      gsap.fromTo(
        words[idx],
        animProps,
        {
          opacity: 1,
          y: 0,
          x: 0,
          scale: 1,
          rotate: 0,
          duration: 0.7 + (random ? Math.random() * 0.4 : 0),
          delay: 0.15 + i * (random ? 0.05 + Math.random() * 0.07 : 0.08),
          ease: 'power2.out',
        }
      );
    });
  }, [text, random]);
  return (
    <span ref={textRef} style={{ display: 'inline-block' }}>
      {text.split(' ').map((word, i) => (
        <span key={i} style={{ display: 'inline-block', opacity: 0, marginRight: 4 }}>{word} </span>
      ))}
    </span>
  );
}

export default function AboutSection() {
    const leftRef = useRef();
    const rightRef = useRef();
    const droneRef = useRef();
    useEffect(() => {
        let lastScrollY = window.scrollY;
        let isFlyingAcross = false;
        let ticking = false;
        const thumbClass = 'scrollbar-thumb-animate';
        const animateScrollbar = () => {
            const scrollbars = document.querySelectorAll('body::-webkit-scrollbar-thumb');
            scrollbars.forEach(sb => sb.classList && sb.classList.add(thumbClass));
            setTimeout(() => {
                scrollbars.forEach(sb => sb.classList && sb.classList.remove(thumbClass));
            }, 400);
        };
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const vh = window.innerHeight;
            const scrollDir = scrollY > lastScrollY ? 1 : -1; // 1 = down, -1 = up
            // Parallax: left moves up slower, right moves up faster
            gsap.to(leftRef.current, { y: -(scrollY - vh) * 0.18, duration: 0.6, overwrite: 'auto' });
            gsap.to(rightRef.current, { y: -(scrollY - vh) * 0.32, duration: 0.6, overwrite: 'auto' });
            // Drone flies in scroll direction: up/right if down
            if (droneRef.current) {
                if (scrollDir === 1) {
                    // Scrolling down: normal fly away
                    gsap.to(droneRef.current, {
                        y: -Math.max(0, scrollY - vh) * 0.45,
                        x: Math.max(0, scrollY - vh) * 0.22,
                        scale: 1 - Math.min((Math.abs(scrollY - vh)) / (vh * 1.2), 0.6),
                        opacity: 1 - Math.min((Math.abs(scrollY - vh)) / (vh * 1.1), 0.7),
                        duration: 0.7,
                        overwrite: 'auto',
                        ease: 'power2.out',
                    });
                    isFlyingAcross = false;
                } else if (!isFlyingAcross && scrollDir === -1 && scrollY < lastScrollY) {
                    // Scrolling up: fly across the screen
                    isFlyingAcross = true;
                    gsap.to(droneRef.current, {
                        x: window.innerWidth * 1.1, // fly far right
                        y: -window.innerHeight * 0.5, // up
                        scale: 0.7,
                        opacity: 0.7,
                        duration: 1.2,
                        ease: 'power2.inOut',
                        onComplete: () => {
                            // Reset after flying across
                            gsap.set(droneRef.current, { x: 0, y: 0, scale: 1, opacity: 1 });
                            isFlyingAcross = false;
                        }
                    });
                }
            }
            lastScrollY = scrollY;
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    return (
        <>
            <div id="about-gradient-bg-global" aria-hidden="true"></div>
            <section className = "about-section">
                <div className="about-flex">
                    <div className="about-left" ref={leftRef}>
                        <h1 className="about-title">About IROD</h1>
                        <p className="about-description">
                            <AnimatedText
                                text={
                                    'IROD (Intelligent Ranger On Duty) is reshaping wildlife conservation with cutting-edge AI. Built in Africa for Africa’s wildest terrains, IROD is an intelligent monitoring system that protects endangered animals, detects poachers, and supports rangers with real-time insights — all without needing constant internet.'
                                }
                                random={true}
                            />
                        </p>
                        <ul className="about-bullets" style={{ marginTop: '1.5rem', marginBottom: 0, paddingLeft: '1.2em', listStyle: 'none' }}>
                            <li style={{ marginBottom: '0.7em', fontSize: '1.08em' }}>
                                <span role="img" aria-label="Identifies">🦁</span> <AnimatedText text={"Identifies and tracks wildlife in real-time"} random={true} />
                            </li>
                            <li style={{ marginBottom: '0.7em', fontSize: '1.08em' }}>
                                <span role="img" aria-label="Monitors">🌿</span> <AnimatedText text={"Monitors animal health, movement, and population"} random={true} />
                            </li>
                            <li style={{ marginBottom: '0.7em', fontSize: '1.08em' }}>
                                <span role="img" aria-label="Alerts">�️</span> <AnimatedText text={"Sends instant alerts for poaching or threats"} random={true} />
                            </li>
                            <li style={{ fontSize: '1.08em' }}>
                                <span role="img" aria-label="Offline">🔋</span> <AnimatedText text={"Works fully offline and syncs when reconnected"} random={true} />
                            </li>
                        </ul>
                        <p className="about-description" style={{ marginTop: '1.5rem' }}>
                            <AnimatedText
                                text={
                                    'IROD is ready to scale and make a difference in wildlife conservation. With your support, we can bring this powerful tool to the frontlines and create lasting impact where it matters most.'
                                }
                                random={true}
                            />
                        </p>
                    </div>
                    <div className="about-right" ref={rightRef}>
                        <div className="about-3d-camera" style={{ position: 'relative', overflow: 'visible' }}>
                            <div ref={droneRef} style={{ width: '100%', height: '100%', willChange: 'transform, opacity' }}>
                                <Canvas camera={{ position: [0, 0, 3.5], fov: 45 }} style={{ width: '100%', height: '100%', minHeight: '260px', borderRadius: '1.2rem', background: 'transparent' }}>
                                    <ambientLight intensity={0.7} />
                                    <directionalLight position={[5, 5, 5]} intensity={0.7} />
                                    {/* <Environment preset="city" /> */}
                                    <DroneModel url="./drone__0701122403_texture.glb" />
                                    <OrbitControls enablePan={false} />
                                </Canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}